"use client";

import { useState, useEffect, useMemo } from "react";
import { useRouter } from "next/navigation";
import ProtectedRoute from "@/components/ProtectedRoute";
import CustomerSidebar from "@/components/CustomerSidebar";
import { useApp } from "@/contexts/AppContext";
import { FaShoppingBag } from "react-icons/fa";
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  doc,
  getDoc,
  getDocs,
  limit,
  startAfter,
  QueryDocumentSnapshot,
  DocumentData,
  getCountFromServer,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import {
  Container,
  Content,
  MainContent,
  PageHeader,
  PageTitle,
  PageSubtitle,
  FilterBar,
  FilterTab,
  OrdersList,
  OrderCard,
  OrderHeader,
  OrderInfo,
  OrderId,
  OrderDate,
  StatusBadge,
  UpdatedBadge,
  OrderItems,
  OrderItemPreview,
  ItemCount,
  OrderFooter,
  OrderTotal,
  ViewButton,
  LoadingContainer,
  Spinner,
  EmptyState,
  EmptyIcon,
  EmptyTitle,
  EmptyText,
  BrowseButton,
  PaginationContainer,
  PaginationInfo,
  PaginationButtons,
  PaginationButton,
} from "./page.styles";

interface OrderItem {
  mealId: string;
  mealTitle: string;
  mealPrice: number;
  mealImage: string;
  artisanId: string;
  artisanName: string;
  quantity: number;
  subtotal: number;
}

interface Order {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  items: OrderItem[];
  subtotal: number;
  deliveryFee: number;
  tax: number;
  total: number;
  status:
    | "pending"
    | "confirmed"
    | "preparing"
    | "delivering"
    | "delivered"
    | "cancelled";
  createdAt: any;
  updatedAt: any;
  recentlyUpdated?: boolean;
}

type FilterType = "all" | "active" | "delivered" | "cancelled";

export default function OrdersPage() {
  const router = useRouter();
  const { user } = useApp();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>("all");

  // Pagination state - per filter
  const [pagination, setPagination] = useState<{
    [key in FilterType]: {
      page: number;
      lastDoc: QueryDocumentSnapshot<DocumentData> | null;
      totalCount: number;
      hasMore: boolean;
      orders: Order[];
    };
  }>({
    all: { page: 1, lastDoc: null, totalCount: 0, hasMore: true, orders: [] },
    active: {
      page: 1,
      lastDoc: null,
      totalCount: 0,
      hasMore: true,
      orders: [],
    },
    delivered: {
      page: 1,
      lastDoc: null,
      totalCount: 0,
      hasMore: true,
      orders: [],
    },
    cancelled: {
      page: 1,
      lastDoc: null,
      totalCount: 0,
      hasMore: true,
      orders: [],
    },
  });
  const ORDERS_PER_PAGE = 5;
  const [isArtisan, setIsArtisan] = useState(false);
  const [checkingArtisan, setCheckingArtisan] = useState(true);
  const [stripeConnected, setStripeConnected] = useState(true);

  // Memoized counts for all filters
  const memoizedCounts = useMemo(
    () => ({
      all: pagination.all.totalCount,
      active: pagination.active.totalCount,
      delivered: pagination.delivered.totalCount,
      cancelled: pagination.cancelled.totalCount,
    }),
    [pagination]
  );

  // Fetch total counts for all filters
  useEffect(() => {
    const fetchOrdersCounts = async () => {
      if (!user?.uid) return;

      try {
        // Count all orders
        const allQuery = query(
          collection(db, "orders"),
          where("userId", "==", user.uid)
        );
        const allSnapshot = await getCountFromServer(allQuery);
        const allCount = allSnapshot.data().count;

        // For "active" - count orders that are not delivered or cancelled
        const activeStatuses = [
          "pending",
          "confirmed",
          "preparing",
          "delivering",
        ];
        let activeCount = 0;
        for (const status of activeStatuses) {
          const statusQuery = query(
            collection(db, "orders"),
            where("userId", "==", user.uid),
            where("status", "==", status)
          );
          const statusSnapshot = await getCountFromServer(statusQuery);
          activeCount += statusSnapshot.data().count;
        }

        // Count delivered orders
        const deliveredQuery = query(
          collection(db, "orders"),
          where("userId", "==", user.uid),
          where("status", "==", "delivered")
        );
        const deliveredSnapshot = await getCountFromServer(deliveredQuery);
        const deliveredCount = deliveredSnapshot.data().count;

        // Count cancelled orders
        const cancelledQuery = query(
          collection(db, "orders"),
          where("userId", "==", user.uid),
          where("status", "==", "cancelled")
        );
        const cancelledSnapshot = await getCountFromServer(cancelledQuery);
        const cancelledCount = cancelledSnapshot.data().count;

        setPagination((prev) => ({
          all: { ...prev.all, totalCount: allCount },
          active: { ...prev.active, totalCount: activeCount },
          delivered: { ...prev.delivered, totalCount: deliveredCount },
          cancelled: { ...prev.cancelled, totalCount: cancelledCount },
        }));
      } catch (error) {
        console.error("Error fetching orders counts:", error);
      }
    };

    fetchOrdersCounts();
  }, [user?.uid]);

  // Fetch paginated orders with filter support
  const fetchOrdersPage = async (
    filterType: FilterType,
    pageNum: number,
    cursor: QueryDocumentSnapshot<DocumentData> | null = null
  ) => {
    if (!user?.uid) return;

    setLoading(true);

    try {
      let ordersQuery;
      const baseConditions: any[] = [
        where("userId", "==", user.uid),
        orderBy("createdAt", "desc"),
      ];

      // Add filter conditions based on filter type
      if (filterType === "active") {
        // For active, we need to fetch multiple statuses - doing separate queries
        // For simplicity with pagination, let's treat "active" filter special
        const activeStatuses = [
          "pending",
          "confirmed",
          "preparing",
          "delivering",
        ];
        // We'll fetch more and filter client-side for now (better approach would be multiple queries)
        // or we fetch all statuses separately - but for pagination let's use "in" query
        // Note: Firestore "in" query limit is 10 items, which is fine for our 4 statuses
        baseConditions.splice(1, 0, where("status", "in", activeStatuses));
      } else if (filterType === "delivered") {
        baseConditions.splice(1, 0, where("status", "==", "delivered"));
      } else if (filterType === "cancelled") {
        baseConditions.splice(1, 0, where("status", "==", "cancelled"));
      }
      // For "all", no status filter needed

      if (cursor) {
        // Fetch next page
        ordersQuery = query(
          collection(db, "orders"),
          ...baseConditions,
          startAfter(cursor),
          limit(ORDERS_PER_PAGE)
        );
      } else {
        // Fetch first page
        ordersQuery = query(
          collection(db, "orders"),
          ...baseConditions,
          limit(ORDERS_PER_PAGE)
        );
      }

      const snapshot = await getDocs(ordersQuery);
      const ordersList: Order[] = [];

      snapshot.forEach((docSnap) => {
        const data = docSnap.data();

        ordersList.push({
          id: docSnap.id,
          userId: data.userId || "",
          userEmail: data.userEmail || "",
          userName: data.userName || "",
          items: data.items || [],
          subtotal: data.subtotal || 0,
          deliveryFee: data.deliveryFee || 0,
          tax: data.tax || 0,
          total: data.total || 0,
          status: data.status || "pending",
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
          recentlyUpdated: false,
        });
      });

      // Update pagination state for this filter
      setPagination((prev) => ({
        ...prev,
        [filterType]: {
          ...prev[filterType],
          page: pageNum,
          lastDoc:
            snapshot.docs.length > 0
              ? snapshot.docs[snapshot.docs.length - 1]
              : null,
          hasMore: snapshot.docs.length === ORDERS_PER_PAGE,
          orders: ordersList,
        },
      }));

      setLoading(false);
    } catch (error) {
      console.error("Error fetching orders page:", error);
      setLoading(false);
    }
  };

  // Load first page on mount and when filter changes
  useEffect(() => {
    if (user?.uid) {
      fetchOrdersPage(filter, 1);
    }
  }, [user?.uid, filter]);

  // Pagination handlers
  const handleNextPage = () => {
    const currentPagination = pagination[filter];
    if (currentPagination.hasMore && currentPagination.lastDoc) {
      fetchOrdersPage(
        filter,
        currentPagination.page + 1,
        currentPagination.lastDoc
      );
    }
  };

  const handlePreviousPage = () => {
    const currentPagination = pagination[filter];
    if (currentPagination.page > 1) {
      // For simplicity, just go back to page 1
      fetchOrdersPage(filter, 1);
    }
  };

  // Calculate pagination info
  const currentPagination = pagination[filter];
  const startItem =
    currentPagination.orders.length > 0
      ? (currentPagination.page - 1) * ORDERS_PER_PAGE + 1
      : 0;
  const endItem = Math.min(
    currentPagination.page * ORDERS_PER_PAGE,
    currentPagination.totalCount
  );

  // Check artisan status
  useEffect(() => {
    const checkArtisanStatus = async () => {
      if (user?.uid) {
        try {
          const userDoc = await getDoc(doc(db, "accounts", user.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            // Check multiple possible indicators of artisan status
            const artisanStatus =
              data.isArtisan === true ||
              data.accountType === "artisan" ||
              data.accountType === "Artisan" ||
              data.verified === "artisan" ||
              data.verified === "Artisan" ||
              data.accountStatus === "artisan" ||
              data.accountStatus === "Artisan" ||
              (data.permitInfo &&
                Object.keys(data.permitInfo).length > 0 &&
                (data.permitInfo.cottageID || data.permitInfo.tradeName));
            setIsArtisan(artisanStatus);

            // Load Stripe connection status for artisans
            if (artisanStatus) {
              setStripeConnected(data.stripeConnected || false);
            }
          }
        } catch (error) {
          console.error("Error checking artisan status:", error);
        }
      }
      setCheckingArtisan(false);
    };

    checkArtisanStatus();
  }, [user]);

  // Set up real-time listener for orders
  useEffect(() => {
    if (!user?.uid) return;

    setLoading(true);

    const ordersQuery = query(
      collection(db, "orders"),
      where("userId", "==", user.uid),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(
      ordersQuery,
      (snapshot) => {
        const ordersList: Order[] = [];
        const previousOrderIds = new Set(orders.map((o) => o.id));

        snapshot.forEach((doc) => {
          const data = doc.data();

          // Check if order was recently updated (within last 5 seconds)
          const updatedTime = data.updatedAt?.toMillis?.() || 0;
          const now = Date.now();
          const recentlyUpdated =
            previousOrderIds.has(doc.id) && now - updatedTime < 5000;

          ordersList.push({
            id: doc.id,
            userId: data.userId || "",
            userEmail: data.userEmail || "",
            userName: data.userName || "",
            items: data.items || [],
            subtotal: data.subtotal || 0,
            deliveryFee: data.deliveryFee || 0,
            tax: data.tax || 0,
            total: data.total || 0,
            status: data.status || "pending",
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
            recentlyUpdated,
          });
        });

        setOrders(ordersList);
        setLoading(false);

        // Show notification for updated orders (not on initial load)
        if (previousOrderIds.size > 0) {
          const updatedOrders = ordersList.filter((o) => o.recentlyUpdated);
          if (
            updatedOrders.length > 0 &&
            "Notification" in window &&
            Notification.permission === "granted"
          ) {
            const order = updatedOrders[0];
            new Notification("Order Updated! 🔔", {
              body: `Order #${order.id.slice(0, 8)} is now: ${order.status}`,
              icon: "/food-icon.png",
            });
          }
        }
      },
      (error) => {
        console.error("❌ Error listening to orders:", error);
        console.error("Error code:", error.code);
        console.error("Error message:", error.message);
        setLoading(false);
      }
    );

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, [user?.uid]);

  // Request notification permission on mount
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  // Use paginated orders from current filter
  const ordersToDisplay = currentPagination.orders;

  // Already filtered by the query, no need to filter again
  const filteredOrders = ordersToDisplay;

  // Use memoized counts for filter tabs
  const getFilterCount = (filterType: FilterType) => {
    return memoizedCounts[filterType];
  };

  // Always show pagination (for all filters)
  const showPagination = ordersToDisplay.length > 0;

  const formatDate = (timestamp: any) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <ProtectedRoute>
        <Container>
          <AppHeader
            showNav={true}
            showGreeting={false}
            showOrders={true}
            currentPage="orders"
            logoLink="/"
          />
          <LoadingContainer>
            <Spinner />
          </LoadingContainer>
        </Container>
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute>
      <Container>
        <AppHeader
          showNav={true}
          showGreeting={false}
          showOrders={true}
          currentPage="orders"
          logoLink="/"
        />

        <Content>
          <CustomerSidebar
            isArtisan={isArtisan}
            checkingArtisan={checkingArtisan}
            stripeConnected={stripeConnected}
            currentPage="orders"
          />

          <MainContent>
            <PageHeader>
              <PageTitle>
                <FaShoppingBag style={{ marginRight: "8px" }} /> My Orders
              </PageTitle>
              <PageSubtitle>
                Track all your orders in real-time (Live Updates 🔴)
              </PageSubtitle>
            </PageHeader>

            <FilterBar>
              <FilterTab
                $active={filter === "all"}
                onClick={() => setFilter("all")}
              >
                All Orders ({getFilterCount("all")})
              </FilterTab>
              <FilterTab
                $active={filter === "active"}
                onClick={() => setFilter("active")}
              >
                Active ({getFilterCount("active")})
              </FilterTab>
              <FilterTab
                $active={filter === "delivered"}
                onClick={() => setFilter("delivered")}
              >
                Delivered ({getFilterCount("delivered")})
              </FilterTab>
              <FilterTab
                $active={filter === "cancelled"}
                onClick={() => setFilter("cancelled")}
              >
                Cancelled ({getFilterCount("cancelled")})
              </FilterTab>
            </FilterBar>

            {filteredOrders.length > 0 ? (
              <OrdersList>
                {filteredOrders.map((order) => (
                  <OrderCard
                    key={order.id}
                    onClick={() => router.push(`/orders/${order.id}`)}
                  >
                    <OrderHeader>
                      <OrderInfo>
                        <OrderId>
                          Order #{order.id.slice(0, 8).toUpperCase()}
                        </OrderId>
                        <OrderDate>{formatDate(order.createdAt)}</OrderDate>
                      </OrderInfo>
                      <div>
                        <StatusBadge $status={order.status}>
                          {order.status}
                          {order.recentlyUpdated && (
                            <UpdatedBadge>UPDATED</UpdatedBadge>
                          )}
                        </StatusBadge>
                      </div>
                    </OrderHeader>

                    <OrderItems>
                      {order.items.slice(0, 3).map((item, index) => (
                        <OrderItemPreview key={index}>
                          <ItemCount>{item.quantity}x</ItemCount>
                          {item.mealTitle}
                        </OrderItemPreview>
                      ))}
                      {order.items.length > 3 && (
                        <OrderItemPreview>
                          +{order.items.length - 3} more
                        </OrderItemPreview>
                      )}
                    </OrderItems>

                    <OrderFooter>
                      <OrderTotal>${order.total.toFixed(2)}</OrderTotal>
                      <ViewButton
                        onClick={(e) => {
                          e.stopPropagation();
                          router.push(`/orders/${order.id}`);
                        }}
                      >
                        View Details →
                      </ViewButton>
                    </OrderFooter>
                  </OrderCard>
                ))}
              </OrdersList>
            ) : (
              <EmptyState>
                <EmptyIcon>📦</EmptyIcon>
                <EmptyTitle>
                  {filter === "all"
                    ? "No orders yet"
                    : filter === "active"
                    ? "No active orders"
                    : filter === "delivered"
                    ? "No delivered orders"
                    : "No cancelled orders"}
                </EmptyTitle>
                <EmptyText>
                  {filter === "all"
                    ? "Start ordering delicious meals from local artisans!"
                    : "Try changing the filter to see other orders."}
                </EmptyText>
                {filter === "all" && (
                  <BrowseButton onClick={() => router.push("/feed")}>
                    Browse Meals
                  </BrowseButton>
                )}
              </EmptyState>
            )}

            {/* Pagination */}
            {showPagination && (
              <PaginationContainer>
                <PaginationInfo>
                  Showing {startItem}-{endItem} of {memoizedCounts[filter]}{" "}
                  orders
                </PaginationInfo>
                <PaginationButtons>
                  <PaginationButton
                    onClick={handlePreviousPage}
                    $disabled={currentPagination.page === 1}
                    disabled={currentPagination.page === 1}
                  >
                    ← Previous
                  </PaginationButton>
                  <PaginationButton
                    onClick={handleNextPage}
                    $disabled={!currentPagination.hasMore}
                    disabled={!currentPagination.hasMore}
                  >
                    Next →
                  </PaginationButton>
                </PaginationButtons>
              </PaginationContainer>
            )}
          </MainContent>
        </Content>
        <Footer />
      </Container>
    </ProtectedRoute>
  );
}
